import {
  Component,
  ViewEncapsulation,
  OnInit,
  ViewChild,
  OnDestroy,
  Input,
  Optional,
  EventEmitter,
  Output,
} from '@angular/core';
import { FormControl, ControlContainer, NgForm } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { CategoryElement } from '@core/data/partial/categories.model';
import { ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface Entry {
  id: string;
  name: string;
}

export function controlContainerFactory(controlContainer?: ControlContainer) {
  return controlContainer;
}

@Component({
  selector: 'xt-portal-dropdown',
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.scss'],
  encapsulation: ViewEncapsulation.None,
  viewProviders: [
    {
      provide: ControlContainer,
      useFactory: controlContainerFactory,
      deps: [[new Optional(), NgForm]],
    },
  ],
  standalone: false,
})
export class DropdownComponent implements OnInit, OnDestroy {
  @ViewChild('multiSelect', { static: true }) multiSelect: MatSelect;

  @Input() noEntriesFoundLabel: string;
  @Input() placeholderLabel: string;
  @Input() label: string;
  @Input() isMultiSelect = false;
  @Input() hasSearch = false;
  @Input() isDisabled = false;
  @Input() required = false;
  @Input() isReadonly = false;
  @Input() isFilled = false;
  @Input() preselectedIds;
  @Input() preselectFirst: boolean = false;
  @Input() isFullWidth: boolean = false;
  @Input() dataCy: string = '';
  @Input() dropdownValue: string | null = '';
  @Input() entries: Entry[] | CategoryElement[] = [];

  @Output() selectedElement = new EventEmitter();
  @Output() selectedElementFull = new EventEmitter();

  public entriesCache: Entry[] = [];

  /** control for the selected entry for multi-selection */
  public entriesCtrl: FormControl = new FormControl();

  /** control for the MatSelect filter keyword multi-selection */
  public entriesFilterCtrl: FormControl = new FormControl();

  /** list of entries filtered by search keyword */
  public filteredEntries: ReplaySubject<Entry[]> = new ReplaySubject<Entry[]>(
    1,
  );

  /** local copy of filtered entries to help set the toggle all checkbox state */
  protected filteredEntriesCache: Entry[] = [];

  /** Subject that emits when the component has been destroyed. */
  protected _onDestroy = new Subject<void>();

  public selected: string[] | null = null;
  public isOpened: boolean = false;

  ngOnInit() {
    // if there are preselected ids in dropdown
    if (this.preselectedIds?.length || this.preselectFirst) {
      // single select requires the whole object and not just the id
      const predefinedEntries = this.entries?.filter(({ id }) =>
        (this.preselectedIds || []).includes(id),
      );
      if (this.preselectFirst) predefinedEntries.unshift(this.entries[0]);
      this.selected = this.isMultiSelect
        ? this.preselectedIds
        : predefinedEntries
          ? predefinedEntries[0]
          : null;

      if (!this.entriesCtrl) {
        this.entriesCtrl = new FormControl(this.selected);
      } else {
        this.entriesCtrl.setValue(this.selected);
      }
      this.entriesCtrl.markAsDirty();
    }
    if (this.entries?.length) {
      this.entriesCache = [...this.entries];

      // load the initial entries list
      this.filteredEntries.next(this.entries.slice());
    }

    // listen for search field value changes
    this.entriesFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterEntries();
      });
  }

  /**
   * selects entries
   *
   * @returns selected entries
   */
  get selectedEntries() {
    const value = Array.isArray(this.entriesCtrl?.value)
      ? this.entriesCtrl?.value
      : [this.entriesCtrl?.value];

    return this.entries?.filter((item) => value.includes(item.id)) || [];
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  /**
   * for reset filter -> sets dropdown values to ''
   *
   */
  ngOnChanges(changes?): void {
    if (changes && changes.preselectedIds) {
      // single select requres the whole object and not just the id
      const predefinedEntries =
        changes.preselectedIds.currentValue &&
        this.entries?.filter(
          ({ id }) =>
            changes.preselectedIds.currentValue.includes(id) ||
            id.includes(changes.preselectedIds?.currentValue),
        );

      this.selected = this.isMultiSelect
        ? changes.preselectedIds.currentValue
        : predefinedEntries
          ? predefinedEntries[0]
          : null;

      this.entriesCtrl.setValue(this.selected);
      this.entriesCtrl.markAsDirty();
    }

    if (this.dropdownValue == null) {
      this.selected = null;
      this.entriesCtrl.reset();
    }
  }

  /**
   * search function,
   */
  filterEntries() {
    if (!this.entries) {
      return;
    }

    // get the search keyword
    let search = this.entriesFilterCtrl.value;
    this.entries = [...this.entriesCache];

    if (!search) {
      this.filteredEntriesCache = this.entries.slice();
      this.filteredEntries.next(this.filteredEntriesCache);
      return;
    } else {
      search = search.toLowerCase();
    }
    // filter the entries
    this.filteredEntriesCache = this.entries.filter(
      (entry) => entry.name.toLowerCase().indexOf(search) > -1,
    );
    this.entries = this.filteredEntriesCache;
  }

  /**
   * removes chip entry from select field
   *
   * @param entry entry to be removed
   */
  onEntryRemoved(entry: string) {
    const entries = this.entriesCtrl.value as string[];
    this.entriesCtrl.setValue(entries.filter((item) => item !== entry)); // To trigger change detection
    this.eventSelection(this.entriesCtrl.value);
    this.eventSelectionObject(this.selectedEntries);
  }

  /**
   * emits event with selection
   * @param event selection
   */
  eventSelection(event) {
    this.selectedElement.emit(event);
  }

  eventSelectionObject(event) {
    this.selectedElementFull.emit(event);
  }
}
